import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { listMyCustomers, type AgentCustomer } from "../lib/agent";
import { useIsAgent } from "../components/AdminPreviewBanner";
import { HelpBox, InfoTip } from "../components/InfoTip";

function fmtEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

function displayName(c: AgentCustomer): string {
  const parts = [c.first_name, c.last_name].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (c.company) return c.company;
  if (c.email) return c.email;
  return `WC-User #${c.wc_customer_id ?? "?"}`;
}

const SOURCE_LABEL: Record<AgentCustomer["attribution_source"], string> = {
  cookie: "Klick / Link",
  manual_admin: "manuell (Admin)",
  import: "Import",
  rolled_up: "übernommen (Sponsor)",
};

export function AgentCustomers() {
  const userIsAgent = useIsAgent();
  const { data, isLoading, error } = useQuery<AgentCustomer[]>({
    queryKey: ["my-customers"],
    queryFn: listMyCustomers,
    enabled: userIsAgent,
  });

  const totalEarned = (data ?? []).reduce((s, c) => s + c.total_earned_cents, 0);
  const totalOrders = (data ?? []).reduce((s, c) => s + c.orders_count, 0);

  return (
    <main className="mx-auto w-full max-w-6xl gradient-radial-warm px-4 py-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center justify-center gap-2">
          <Users className="size-6 text-accent-orange" />
          Meine Kunden
          <InfoTip title="Wer taucht hier auf?" maxWidth={280}>
            Alle Personen, die über deinen Link / deinen Shop registriert
            wurden. Ein Kunde bleibt <strong>lebenslang</strong> dir zugeordnet
            — jede weitere Bestellung verdient dir automatisch weiter Geld.
          </InfoTip>
        </h2>
        <p className="mt-2 text-sm text-ink-muted">
          Personen, die du für DP Connect gewonnen hast.
        </p>
      </div>

      {isLoading && <div className="text-sm text-ink-muted">Lade…</div>}

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {error instanceof Error ? error.message : "Fehler"}
        </div>
      )}

      {data && data.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line bg-white/40 p-12 text-center">
          <p className="text-sm text-ink-muted">
            Noch keine Kunden. Sobald jemand über deinen Link registriert,
            taucht er hier auf.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            <SummaryCard label="Kunden gesamt" value={data.length.toString()} />
            <SummaryCard label="Bestellungen" value={totalOrders.toString()} />
            <SummaryCard label="Verdient" value={fmtEur(totalEarned)} highlight />
          </div>

          <HelpBox kind="info" collapsible title="💡 Wie liest sich diese Liste?">
            Jede Zeile ist ein Kunde. „Bestellungen" zählt alle abgeschlossenen
            Bestellungen; „Verdient" summiert deine Provisionen aus diesen
            Käufen (nur was DU bekommen hast, keine Sponsor-Anteile). Wenn du
            keinen Namen siehst, hat der Kunde direkt über dpconnect.de
            registriert (nicht über deine Subdomain) — dann haben wir nur die
            Email.
          </HelpBox>

          <div className="mt-4 overflow-x-auto rounded-2xl border border-line bg-white">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-cream-soft/40 text-ink-muted">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Kunde</th>
                  <th className="text-left font-medium px-3 py-2">Firma / Stadt</th>
                  <th className="text-left font-medium px-3 py-2">Kontakt</th>
                  <th className="text-left font-medium px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      Quelle
                      <InfoTip title="Wie kam der Kunde zu dir?" maxWidth={220}>
                        <em>Klick / Link</em> = normale Attribution über deinen
                        Empfehlungs-Link. <em>Manuell</em> = Admin hat zugeordnet.
                        <em> Übernommen</em> = Sponsor hat den Kunden abgegeben.
                      </InfoTip>
                    </span>
                  </th>
                  <th className="text-center font-medium px-3 py-2">Bestellungen</th>
                  <th className="text-right font-medium px-3 py-2">Verdient</th>
                  <th className="text-left font-medium px-3 py-2">Seit</th>
                </tr>
              </thead>
              <tbody>
                {data.map((c) => (
                  <tr key={c.attribution_id} className="border-t border-line">
                    <td className="px-3 py-2">
                      <div className="font-medium">{displayName(c)}</div>
                      {c.wc_customer_id && (
                        <div className="text-[10px] font-mono text-ink-muted">
                          WC #{c.wc_customer_id}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-soft">
                      {c.company && <div>{c.company}</div>}
                      {c.city && <div className="text-ink-muted">{c.city}</div>}
                      {!c.company && !c.city && <span className="text-ink-muted">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {c.email ? (
                        <a
                          href={`mailto:${c.email}`}
                          className="text-accent-orange hover:underline break-all"
                        >
                          {c.email}
                        </a>
                      ) : (
                        <span className="text-ink-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">
                      {SOURCE_LABEL[c.attribution_source] ?? c.attribution_source}
                    </td>
                    <td className="px-3 py-2 text-center font-mono">
                      {c.orders_count}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">
                      {c.total_earned_cents > 0 ? fmtEur(c.total_earned_cents) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">
                      {new Date(c.attributed_at).toLocaleDateString("de-DE")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}

function SummaryCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="rounded-2xl border border-line bg-white p-4">
      <div className="text-xs uppercase tracking-wide font-semibold text-ink-muted">
        {label}
      </div>
      <div
        className={`mt-2 text-2xl font-semibold tracking-tight font-mono ${
          highlight ? "text-emerald-700" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}
